import _ from "lodash";
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";


export type FormNotificationsStateType = {
    notificationListFilterText: string;
    viewSelected: boolean;
    viewOptionState: ViewOptionStateEnum;
    organizationID: number | undefined;
    defaultOrganizationID: number | undefined;
};

enum FormNotificationsActionType {
    NotificationListFilterChanged = "NOTIFICATIONLIST_FILTER_CHANGED",
    ViewSelectedChanged = "VIEW_SELECTED_CHANGED",
    OrganizationChanged = 'ORGANIZATION_CHANGED',
    ResetChanged = "RESET_CHANGED",
}

type FormNotificationsAction = {
    type: FormNotificationsActionType.NotificationListFilterChanged;
    payload: string;
} | {
    type: FormNotificationsActionType.ViewSelectedChanged;
    payload: boolean;
} | {
    type: FormNotificationsActionType.OrganizationChanged;
    payload: number | undefined;
} | {
    type: FormNotificationsActionType.ResetChanged;
    payload: void;
};

const initialState: FormNotificationsStateType = {
    notificationListFilterText: '',
    viewSelected: false,
    viewOptionState: ViewOptionStateEnum.Default,
    organizationID: undefined,
    defaultOrganizationID: undefined
};

const optionsChanged = (newState: FormNotificationsStateType, 
    defaultState: FormNotificationsStateType) => 
    newState.notificationListFilterText !== defaultState.notificationListFilterText ||
    newState.viewSelected !== defaultState.viewSelected ||
    newState.organizationID !== newState.defaultOrganizationID;

function tierNotificationReducer(state: FormNotificationsStateType, action: FormNotificationsAction): FormNotificationsStateType {
    let copy = _.cloneDeep(state);    

    switch (action.type) {

        case FormNotificationsActionType.NotificationListFilterChanged:
            copy.notificationListFilterText = action.payload;
            break;
  
        case FormNotificationsActionType.ViewSelectedChanged:
            copy.viewSelected = action.payload;
            break;

        case FormNotificationsActionType.OrganizationChanged:
            copy.organizationID = action.payload;
            if (copy.defaultOrganizationID === undefined) {
                copy.defaultOrganizationID = action.payload;
            }
            break;
                
        case FormNotificationsActionType.ResetChanged:
            copy = _.cloneDeep(initialState);
            break;

        default:
            return state;
    }

    copy.viewOptionState = optionsChanged(copy, initialState) ? 
                            ViewOptionStateEnum.Changed : 
                            ViewOptionStateEnum.Default;

    return copy;
}

export type UseFormNotificationsStateType = {
    state: FormNotificationsStateType;
    setNotificationListFilterText: (value: string) => void;
    setViewSelected: (value: boolean) => void;
    setOrganization: (value: number | undefined) => void;
    resetState: () => void;
};

export function useFormNotificationListsState(): UseFormNotificationsStateType {

    const [state, dispatch] = useReducer(tierNotificationReducer, initialState);

    const setNotificationListFilterText = useCallback((value: string) => dispatch({
        type: FormNotificationsActionType.NotificationListFilterChanged,
        payload: value
    }), []);

    const setViewSelected = useCallback((value: boolean) => dispatch({
        type: FormNotificationsActionType.ViewSelectedChanged,
        payload: value
    }), []);

    const setOrganization = useCallback((value: number | undefined) => dispatch({
        type: FormNotificationsActionType.OrganizationChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: FormNotificationsActionType.ResetChanged,
        payload: undefined
    }), [])

    return {
        state,
        setNotificationListFilterText,
        setViewSelected,
        setOrganization,
        resetState
    };
}
